import i18n from '../../../i18n/i18n';
import { tr } from '../../../i18n/i18n';
import React, { useRef, useState } from 'react';
import { AppButton } from '../../atoms';
import { PrimaryModal } from '../../Modals';
import type { ModalHandle } from '../../Modals/primaryModal';
import { truncateFileName } from '../../../utility';
import { useAppDispatch } from '../../../redux/stateHooks';
import { showNotification } from '../../../redux/slices/notification.slice';
import { downloadTemplate, uploadFile } from '../../../redux/action/bulkUpload.action';
import { BULLMQ_JOBS } from '../../../constant/message-queue.constant';
import { BULK_UPLOAD_CONFIG } from '../../../services/bulkUpload/config';
import { useTranslation } from 'react-i18next';

interface BulkUploadModalProps {
  /**
   * Configuration key to determine which API to call
   * (e.g., 'STUDENT_BULK_UPLOAD', 'CURRICULUM_BULK_UPLOAD', etc.)
   */
  configKey: BULLMQ_JOBS;

  /**
   * Configuration title for display (e.g., 'Student', 'Curriculum')
   */
  configTitle: string;

  /**
   * Optional callback when upload is successful with response message
   */
  onUploadSuccess?: (message?: string) => void;
}

/**
 * Reusable Bulk Upload Modal Component
 *
 * Handles bulk upload operations for different entity types (students, curriculum, etc.)
 * by switching API calls based on the configKey prop.
 *
 * @example
 * ```tsx
 * const modalRef = useRef<ModalHandle>(null);
 *
 * <AppButton
 *   label={tr('fe_bulk_upload.bulk_upload', "Bulk Upload")}
 *   onClick={() => modalRef.current?.openModal()}
 * />
 *
 * <BulkUploadModal
 *   ref={modalRef}
 *   configKey="STUDENT_BULK_UPLOAD"
 *   configTitle="Student"
 *   onUploadSuccess={() => console.log('Upload successful')}
 * />
 * ```
 */
const BulkUploadModal = React.forwardRef<ModalHandle, BulkUploadModalProps>(
  ({ configKey, configTitle, onUploadSuccess }, ref) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const internalRef = useRef<ModalHandle>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);

    // Combine external and internal refs
    React.useImperativeHandle(ref, () => ({
      openModal: () => {
        internalRef.current?.openModal();
      },
      closeModal: () => {
        internalRef.current?.closeModal();
        handleCloseModal();
      },
    }));

    /**
     * Handle download template using centralized action
     */
    const handleDownloadTemplate = async () => {
      try {
        const moduleConfig = BULK_UPLOAD_CONFIG[configKey];
        if (!moduleConfig) {
          dispatch(
            showNotification({
              severity: 'error',
              message: i18n.t('toast.template_dl_unavailable', 'Template download not available for {{title}}.', { title: configTitle }),
            }),
          );
          return;
        }

        await dispatch(downloadTemplate(configKey)).unwrap();
        dispatch(
          showNotification({
            severity: 'success',
            message: i18n.t('toast.template_downloaded', '{{title}} template downloaded successfully.', { title: configTitle }),
          }),
        );
      } catch (error: any) {
        dispatch(
          showNotification({
            severity: 'error',
            message: error?.message || tr('g.failed_to_download_template', 'Failed to download template.'),
          }),
        );
      }
    };

    /**
     * Handle file upload using centralized action
     */
    const handleFileUpload = async () => {
      if (!selectedFile) return;

      const moduleConfig = BULK_UPLOAD_CONFIG[configKey];
      if (!moduleConfig) {
        dispatch(
          showNotification({
            severity: 'error',
            message: i18n.t('toast.bulk_upload_unavailable', 'Bulk upload not available for {{title}}.', { title: configTitle }),
          }),
        );
        return;
      }

      const formData = new FormData();
      formData.append('file', selectedFile);

      setIsUploading(true);

      try {
        const response = await dispatch(uploadFile({ moduleKey: configKey, formData })).unwrap();

        // Extract message from response or use default
        const successMessage =
          response?.message ||
          i18n.t('toast.bulk_upload_initiated', '{{title}} bulk upload initiated successfully.', { title: configTitle });

        // Success - reset state and close modal
        setSelectedFile(null);
        setIsUploading(false);
        internalRef.current?.closeModal();
        onUploadSuccess?.(successMessage);
      } catch (error: any) {
        dispatch(
          showNotification({
            severity: 'error',
            message: error?.message || error || tr('g.failed_to_upload_file_2', 'Failed to upload file.'),
          }),
        );
        setIsUploading(false);
      }
    };

    /**
     * Handle file selection
     */
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Validate file type
      const validTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv',
      ];

      if (!validTypes.includes(file.type)) {
        dispatch(
          showNotification({
            severity: 'error',
            message: i18n.t('msg.please_upload_an_excel_file_xlsx_xls_or_csv_file', 'Please upload an Excel file (.xlsx, .xls) or CSV file.'),
          }),
        );
        return;
      }

      setSelectedFile(file);
    };

    /**
     * Reset modal state when closing
     */
    const handleCloseModal = () => {
      setSelectedFile(null);
      setIsUploading(false);
      internalRef.current?.closeModal();
    };

    /**
     * Modal content
     */
    const modalContent = (
      <>
        <p className="mb-6 text-sm">
          {t('bulk.efficiently_add', 'To efficiently add multiple {{type}}s, upload an Excel file. Please ensure your file is correctly formatted using our template.', { type: configTitle.split('-')[0].trim().toLowerCase() })}
        </p>

        {/* File Upload Section */}
        <div className="mb-6 p-4 border border-gray-300 rounded-md bg-gray-50">
          <label htmlFor="file-upload" className="block text-gray-700 text-base font-bold mb-3">
            {t('bulk.select_file', '1. Select Your File')}
          </label>
          <div className="flex items-center gap-4">
            {/* Hidden native file input */}
            <input
              type="file"
              id="file-upload"
              accept=".xlsx, .xls, .csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, text/csv"
              onChange={handleFileChange}
              className="hidden"
              disabled={isUploading}
            />

            {/* Styled label as a button */}
            <label
              htmlFor="file-upload"
              className={`py-2 px-4 bg-blue-100 text-blue-700 font-semibold rounded-full text-sm transition ${
                isUploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-blue-200'
              }`}
            >{t('g.choose_file', 'Choose File')}</label>

            {/* Display selected file name */}
            <span className="text-sm text-gray-600 w-[70%] truncate">
              {selectedFile ? truncateFileName(selectedFile.name, 50) : tr('g.no_file_chosen', 'No file chosen')}
            </span>
          </div>

          <p className="text-sm mt-2">{t('common.accepted_formats_xlsx_csv', 'Accepted formats: XLSX, XLS, CSV')}</p>
          <div className="mt-4">
            <AppButton
              onClick={handleFileUpload}
              label={t('common.label_upload_file', 'Upload File')}
              loading={isUploading}
              disabled={!selectedFile || isUploading}
            />
          </div>
        </div>

        {/* Template Download and Instructions */}
        <div className="mb-8 p-4 border border-gray-300 rounded-md bg-gray-50">
          <p className="block text-base font-bold mb-3">{t('bulk.need_template', '2. Need a Template?')}</p>
          <p className="mb-4 text-sm">
            {t('bulk.template_help', 'Download our template to ensure your data matches the required format. This helps prevent upload errors.')}
          </p>
          <AppButton
            onClick={handleDownloadTemplate}
            label={t('common.button_download_template', 'Download Template (.xlsx)')}
            disabled={isUploading}
          />
        </div>
      </>
    );

    return (
      <PrimaryModal
        ref={internalRef}
        title={configTitle}
        content={modalContent}
        width="40vw"
        showHeader={true}
        onClose={handleCloseModal}
      />
    );
  },
);

BulkUploadModal.displayName = 'BulkUploadModal';

export default BulkUploadModal;
